import { TF } from 'react-translator-component';

import { IntegrationStation, StationIssue } from '../../../../types/responses/Interoperability';

/**
 * Os textos da aba Estações (MV2-1706).
 *
 * Duas frases por problema: o rótulo diz o que falta, e a consequência diz o que
 * acontece do lado do parceiro. Sem a segunda, a tela é uma lista de campos vazios e
 * ninguém sabe qual deles vale interromper o dia para corrigir.
 *
 * Usa TF e não T porque o retorno é declarado string: T devolve o array de nós do
 * react-html-parser, que funciona em JSX mas estoura na primeira operação de string.
 */

export type IssueSeverity = 'BLOCKING' | 'ATTENTION';

const LABELS: Record<StationIssue, string> = {
  DISABLED: 'Disabled',
  MISSING_COORDINATES: 'No coordinates',
  MISSING_ADDRESS: 'No address',
  NO_CONNECTOR: 'No connector',
  MISSING_TARIFF: 'No tariff',
  NEVER_CONNECTED: 'Never connected',
};

const CONSEQUENCES: Record<StationIssue, string> = {
  DISABLED: 'It does not show up in the catalogue the partner reads.',
  MISSING_COORDINATES:
    'The partner app searches by radius, so the station never appears in the results.',
  MISSING_ADDRESS: 'The partner displays the station without saying where it is.',
  NO_CONNECTOR: 'There is nothing to start: the partner user cannot charge here.',
  MISSING_TARIFF: 'The partner does not know the price it will charge its own user.',
  NEVER_CONNECTED: 'It has never reported since it was registered — different from being offline now.',
};

/**
 * BLOCKING é o que impede o parceiro de usar a estação; ATTENTION é o que deixa a
 * estação usável, porém errada ou incompleta.
 *
 * Sem tarifa fica em ATTENTION e não em BLOCKING porque a recarga inicia — o
 * prejuízo é de preço, não de disponibilidade.
 */
const SEVERITIES: Record<StationIssue, IssueSeverity> = {
  DISABLED: 'BLOCKING',
  MISSING_COORDINATES: 'BLOCKING',
  NO_CONNECTOR: 'BLOCKING',
  MISSING_ADDRESS: 'ATTENTION',
  MISSING_TARIFF: 'ATTENTION',
  NEVER_CONNECTED: 'ATTENTION',
};

/** Ordem de exibição: primeiro o que impede o uso. */
export const ISSUE_ORDER: StationIssue[] = [
  'DISABLED',
  'MISSING_COORDINATES',
  'NO_CONNECTOR',
  'MISSING_TARIFF',
  'MISSING_ADDRESS',
  'NEVER_CONNECTED',
];

export const issueLabel = (issue: StationIssue): string => TF(LABELS[issue] || issue);

export const issueConsequence = (issue: StationIssue): string => TF(CONSEQUENCES[issue] || '');

export const issueSeverity = (issue: StationIssue): IssueSeverity => SEVERITIES[issue] || 'ATTENTION';

export const SEVERITY_STYLE: Record<IssueSeverity, { bg: string; fg: string; dot: string }> = {
  BLOCKING: { bg: '#fdecec', fg: '#b02525', dot: '#c8352f' },
  ATTENTION: { bg: '#fdeee4', fg: '#a8500f', dot: '#e0700f' },
};

/** A cor da linha vem do pior problema dela: uma estação com dois problemas não é "meio ok". */
export const worstSeverity = (issues: StationIssue[]): IssueSeverity | null => {
  if (!issues || issues.length === 0) {
    return null;
  }

  return issues.some((issue) => issueSeverity(issue) === 'BLOCKING') ? 'BLOCKING' : 'ATTENTION';
};

/** Problemas na ordem de exibição, ignorando valor que a tela ainda não conheça. */
export const sortedIssues = (issues: StationIssue[]): StationIssue[] => {
  const known = ISSUE_ORDER.filter((issue) => issues.includes(issue));
  const unknown = issues.filter((issue) => !ISSUE_ORDER.includes(issue));

  return [...known, ...unknown];
};

/**
 * Cidade e estado, ou a falta deles dita em voz alta.
 *
 * Célula vazia parece falha de carregamento — e a aba existe justamente para
 * apontar o que está vazio.
 */
export const stationLocation = (station: IntegrationStation): string => {
  const parts = [station.city, station.state].filter(Boolean);

  return parts.length > 0 ? parts.join(' / ') : TF('no address');
};

/**
 * Como chamar a estação.
 *
 * Nome, senão número, senão o começo do id: a pessoa precisa conseguir dizer de qual
 * estação está falando ao pedir a correção para quem cadastrou.
 */
export const stationTitle = (station: IntegrationStation): string => {
  if (station.name && station.name.trim()) {
    return station.name;
  }

  if (station.charge_box_number !== null && station.charge_box_number !== undefined) {
    return `${TF('Station')} ${station.charge_box_number}`;
  }

  return (station.charge_box_id || '').slice(0, 8);
};

/** "há 3 min", "há 2 dias" — o suficiente para dizer se o sinal é recente. */
export const heartbeatLabel = (station: IntegrationStation): string => {
  if (!station.last_heartbeat) {
    return TF('never');
  }

  const moment = new Date(station.last_heartbeat);

  if (Number.isNaN(moment.getTime())) {
    return TF('never');
  }

  const minutes = Math.max(0, Math.round((Date.now() - moment.getTime()) / 60000));

  if (minutes < 60) {
    return `${minutes} min`;
  }

  const hours = Math.round(minutes / 60);

  return hours < 48 ? `${hours} h` : `${Math.round(hours / 24)} d`;
};
